import React, { ComponentProps, useState } from 'react';

import { Alert, AlertDescription, Grid, Spacer } from '@zapier/design-system';

import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import VersionPicker from 'app/developer-v3/components/VersionPicker';

import EventTable from './EventTable';
import MonitorGraph from './MonitorGraph';

import type { EventType } from './types';
import type { SemVer } from 'app/developer-v3/types/version';

export type EventFiltersType = {
  app_version?: string;
  category?: string;
  end_date?: string;
  other_email?: string;
  period?: string;
  period_index?: string;
  start_date?: string;
};

type Props = {
  appEventStats: ComponentProps<typeof MonitorGraph>['stats'];
  appEvents: Array<EventType>;
  appId: number;
  appVersion?: SemVer;
  clearAppEvents: () => null;
  clearErrors: () => null;
  errors: Array<{}>;
  hasAcceptedTerms: boolean;
  isStaff: boolean;
  loadAppEventStats: (filters: EventFiltersType) => null;
  loadAppEvents: (filters: EventFiltersType) => null;
  loadingStates: { appEvents: boolean; appEventStats: boolean };
  trackPageEvent: ({
    event: string,
    event_data: { app_id: number, filters: EventFiltersType },
  }) => null;
};

function MonitoringPage({
  appEventStats,
  appEvents,
  appVersion,
  appId,
  clearAppEvents,
  clearErrors,
  errors,
  hasAcceptedTerms,
  isStaff,
  loadAppEventStats,
  loadAppEvents,
  loadingStates,
  trackPageEvent,
}: Props) {
  const [version, setVersion] = useState<SemVer>(appVersion);

  return (
    <ManagerLayout
      renderDescription={() => (
        <Grid alignItems="end" columns="70% 1fr auto">
          <Alert status="info" title={`Show events for version ${version}`}>
            <AlertDescription>
              Select a data point on the chart to view its events and details.
              All times are in UTC.
            </AlertDescription>
          </Alert>
          <div></div>
          <VersionPicker
            hasAllVersions={false}
            initialVersion={version}
            integrationId={appId}
            isLabelVisible={true}
            onChange={(versionNext: SemVer) => setVersion(versionNext)}
          />
        </Grid>
      )}
      title="Monitoring"
    >
      <Spacer height={20} />
      <Grid columns={1} gap={20}>
        {errors.length !== 0 && (
          <Alert onDismiss={clearErrors} status="error">
            We had a problem fetching the data. Please try again.
          </Alert>
        )}
        <MonitorGraph
          appId={appId}
          appVersion={version}
          clearAppEvents={clearAppEvents}
          clearErrors={clearErrors}
          hasAcceptedTerms={hasAcceptedTerms}
          isLoading={loadingStates.appEventStats}
          isStaff={isStaff}
          loadAppEvents={loadAppEvents}
          loadAppEventStats={loadAppEventStats}
          stats={appEventStats}
          trackPageEvent={trackPageEvent}
        />
        <EventTable events={appEvents} isLoading={loadingStates.appEvents} />
      </Grid>
    </ManagerLayout>
  );
}

MonitoringPage.defaultProps = {
  errors: [],
  loadingStates: {
    appEvents: false,
    appEventStats: false,
  },
};

export default MonitoringPage;
